import React, { useState, useEffect } from 'react';

/**
 * Component to display rule buttons when game generation is in progress
 *
 * @param {Object} props - Component props
 * @param {Object} props.rulesModel - Rules model containing the rules to display
 * @param {Function} props.onDisplayRule - Function to call when a rule is clicked, receives rule index
 */
const RulesButtons = ({ rulesModel, onDisplayRule }) => {
  // If no rules model provided, don't render anything
  if (!rulesModel) return null;

  // Extract rules from the model
  let rules = [];

  if (Array.isArray(rulesModel)) {
    // If it's an array, use each item
    rules = rulesModel;
  } else if (typeof rulesModel === 'object') {
    // If it's an object, use each key-value pair
    rules = Object.entries(rulesModel).map(([key, value]) => ({
      id: key,
      name: typeof value === 'object' && value?.name ? value.name : key
    }));
  }

  // If no rules after processing, don't render anything
  if (!rules.length) return null;

  // State for pagination/carousel
  const [currentPage, setCurrentPage] = useState(0);
  const rulesPerPage = 4; // Show 4 rules at a time
  const totalPages = Math.ceil(rules.length / rulesPerPage);

  // Get current visible rules
  const currentRules = rules.slice(
    currentPage * rulesPerPage,
    (currentPage + 1) * rulesPerPage
  );

  // Go to specific page
  const goToPage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  // Handle next/previous page
  const nextPage = () => {
    setCurrentPage(prev => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
  };

  // Handle rule click - calculate the actual index in the full rules array
  const handleRuleClick = (localIndex) => {
    const actualRuleIndex = currentPage * rulesPerPage + localIndex;
    if (onDisplayRule) {
      onDisplayRule(actualRuleIndex);
    }
  };

  return (
    <div className="rules-buttons-container" style={{
      width: '100%',
      padding: '0 20px',
      marginTop: '20px',
      marginBottom: '20px',
    }}>
      <h3 style={{
        textAlign: 'center',
        color: 'var(--text)',
        marginBottom: '15px',
        fontSize: 'clamp(18px, 4vw, 22px)',
      }}>
        Règles du jeu
      </h3>

      <div style={{ position: 'relative' }}>
        {/* Rules carousel */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
        }}>
          {currentRules.map((rule, index) => (
            <button
              key={index}
              className="rule-button"
              style={{
                padding: '15px 20px',
                borderRadius: '15px',
                border: 'none',
                background: 'rgba(90,88,255,0.4)',
                color: 'white',
                fontWeight: '500',
                fontSize: 'clamp(16px, 3vw, 18px)',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                animation: 'fadeIn 0.4s ease-out',
              }}
              onClick={() => handleRuleClick(index)}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 15px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.1)';
              }}
            >
              {/* Display rule name depending on data structure */}
              {rule.key}
            </button>
          ))}
        </div>

        {/* Navigation buttons - only show if we have multiple pages */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px',
          }}>
            {/* Previous button */}
            <button
              onClick={prevPage}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                background: 'linear-gradient(135deg, rgba(90,88,255,0.1), rgba(43,72,255,0.05))',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                color: '#7b78ff',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 2px 5px rgba(0,0,0,0.1)';
              }}
            >
              <span style={{ fontSize: '18px' }}>←</span>
            </button>

            {/* Page indicators */}
            <div style={{
              display: 'flex',
              gap: '8px',
            }}>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToPage(idx)}
                  aria-label={`Page ${idx + 1}`}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    border: 'none',
                    background: currentPage === idx ? '#7b78ff' : 'rgba(123, 120, 255, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: currentPage === idx ? 'scale(1.2)' : 'scale(1)',
                  }}
                />
              ))}
            </div>

            {/* Next button */}
            <button
              onClick={nextPage}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                background: 'linear-gradient(135deg, rgba(90,88,255,0.1), rgba(43,72,255,0.05))',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                color: '#7b78ff',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = '0 2px 5px rgba(0,0,0,0.1)';
              }}
            >
              <span style={{ fontSize: '18px' }}>→</span>
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default RulesButtons;
